import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'

interface Machine {
  id: number
  name: string
  location?: string
  hal?: string
}

export const ProblemReporting: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [machines, setMachines] = useState<Machine[]>([])
  const [formData, setFormData] = useState({
    datetime: new Date().toLocaleString('nl-NL'),
    productcode: '',
    machine_id: '',
    argumentation: '',
    solutionFound: false,
    solutionDescription: ''
  })
  const [photos, setPhotos] = useState<FileList | null>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    loadMachines()
  }, [])

  const loadMachines = async () => {
    try {
      const { data, error } = await supabase
        .from('machines')
        .select('*')
        .order('name')

      if (error) {
        console.error('Error loading machines:', error)
        return
      }

      setMachines(data || [])
    } catch (error) {
      console.error('Error loading machines:', error)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSolutionChange = (value: boolean) => {
    setFormData(prev => ({
      ...prev,
      solutionFound: value,
      solutionDescription: value ? prev.solutionDescription : ''
    }))
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhotos(e.target.files)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Convert photos to base64 or store them (simplified approach)
      const photoUrls: string[] = []
      
      if (photos) {
        for (let i = 0; i < photos.length; i++) {
          const file = photos[i]
          const reader = new FileReader()
          
          const photoData = await new Promise<string>((resolve) => {
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          })
          
          photoUrls.push(photoData)
        }
      }

      const problemData = {
        datum_tijd: new Date().toISOString(),
        productcode: formData.productcode,
        machine_id: parseInt(formData.machine_id) || null,
        argumentatie: formData.argumentation,
        oplossing_gevonden: formData.solutionFound,
        oplossing_omschrijving: formData.solutionFound ? formData.solutionDescription : null,
        fotos: photoUrls,
        gebruiker_naam: user?.name || 'Onbekend',
        created_by: user?.id || 'unknown',
        created_by_name: user?.name || 'Onbekend'
      }

      const { error } = await supabase
        .from('probleem_meldingen')
        .insert([problemData])

      if (error) {
        console.error('Error submitting problem:', error)
        alert('Er ging iets mis bij het versturen van het probleem.')
        return
      }

      setShowSuccess(true)
      
      // Reset form
      setFormData({
        datetime: new Date().toLocaleString('nl-NL'),
        productcode: '',
        machine_id: '',
        argumentation: '',
        solutionFound: false,
        solutionDescription: ''
      })
      setPhotos(null)

      // Auto close success message and redirect
      setTimeout(() => {
        setShowSuccess(false)
        navigate('/dashboard/teamleader')
      }, 2000)

    } catch (error) {
      console.error('Error submitting problem:', error)
      alert('Er ging iets mis bij het versturen van het probleem.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="form-page min-h-screen py-6 sm:py-10 px-3 sm:px-4">
      {/* Success Popup */}
      {showSuccess && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50"></div>
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white p-6 rounded-xl shadow-xl border-2 border-[#920000] w-[calc(100%-2rem)] max-w-sm">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#920000] mx-auto flex items-center justify-center">
                <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-[#920000] mt-4">Formulier Verstuurd!</h2>
              <p className="text-gray-600 mt-2">Je probleem is succesvol gemeld.</p>
            </div>
          </div>
        </>
      )}

      <div className="form-surface max-w-2xl mx-auto p-4 sm:p-7">
        <p className="eyebrow">Productie · incident</p>
        <h1 className="page-title mt-2 mb-1">Probleem melden</h1>
        <p className="page-subtitle mb-6">Leg de situatie duidelijk vast zodat het team snel kan opvolgen.</p>
        
        <form onSubmit={handleSubmit} className="professional-form space-y-4">
          <div>
            <label htmlFor="datetime" className="block text-[#920000] font-bold mb-1">Datum en Tijd:</label>
            <input 
              type="text" 
              id="datetime"
              name="datetime"
              value={formData.datetime}
              readOnly 
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg bg-gray-50 focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="productcode" className="block text-[#920000] font-bold mb-1">Productcode:</label>
            <input 
              type="text" 
              id="productcode"
              name="productcode"
              value={formData.productcode}
              onChange={handleInputChange}
              placeholder="Vul productcode in" 
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
          </div>

          <div>
            <label htmlFor="machine_id" className="block text-[#920000] font-bold mb-1">Selecteer Machine:</label>
            <select 
              id="machine_id"
              name="machine_id"
              value={formData.machine_id}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            >
              <option value="">Selecteer een machine...</option>
              {machines.map(machine => (
                <option key={machine.id} value={machine.id}>
                  {machine.name} {machine.location && `- ${machine.location}`} {machine.hal && `(${machine.hal})`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="photoUpload" className="block text-[#920000] font-bold mb-1">Foto upload (meerdere foto's toegestaan):</label>
            <input 
              type="file" 
              id="photoUpload"
              multiple 
              accept="image/*"
              onChange={handlePhotoChange}
              className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
          </div>

          <div>
            <label htmlFor="argumentation" className="block text-[#920000] font-bold mb-1">Argumentatie:</label>
            <textarea 
              id="argumentation"
              name="argumentation"
              value={formData.argumentation}
              onChange={handleInputChange}
              placeholder="Voer argumentatie in" 
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32 resize-y focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
            />
          </div>

          <div>
            <p className="block text-[#920000] font-bold mb-1">Oplossing gevonden?</p>
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center">
                <input 
                  type="radio" 
                  id="solutionYes"
                  name="solutionFound"
                  checked={formData.solutionFound === true}
                  onChange={() => handleSolutionChange(true)}
                  className="w-5 h-5 text-[#920000] border-gray-300 focus:ring-[#920000]"
                />
                <label htmlFor="solutionYes" className="ml-2 text-gray-700">Ja</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="radio" 
                  id="solutionNo"
                  name="solutionFound"
                  checked={formData.solutionFound === false}
                  onChange={() => handleSolutionChange(false)}
                  className="w-5 h-5 text-[#920000] border-gray-300 focus:ring-[#920000]"
                />
                <label htmlFor="solutionNo" className="ml-2 text-gray-700">Nee</label>
              </div>
            </div>
          </div>

          {formData.solutionFound && (
            <div>
              <label htmlFor="solutionDescription" className="block text-[#920000] font-bold mb-1">Oplossing:</label>
              <input 
                type="text" 
                id="solutionDescription"
                name="solutionDescription"
                value={formData.solutionDescription}
                onChange={handleInputChange}
                placeholder="Beschrijf de oplossing" 
                required={formData.solutionFound}
                className="w-full px-3 py-2 min-h-touch border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#920000]/50"
              />
            </div>
          )}

          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full py-3 mt-6 bg-gradient-to-r from-[#920000] to-[#6F0000] text-white font-medium rounded-lg shadow hover:from-[#6F0000] hover:to-[#6F0000] transition duration-300 ease-in-out disabled:opacity-50"
          >
            {isSubmitting ? 'Versturen...' : 'Verzenden'}
          </button>
        </form>

        <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-0 mt-6">
          <button
            onClick={() => navigate(-1)}
            className="px-5 py-2 min-h-touch bg-[#920000] text-white font-medium rounded-lg shadow hover:bg-[#6F0000] transition duration-300 ease-in-out"
          >
            Terug
          </button>
          <button
            onClick={() => navigate('/dashboard/teamleader')}
            className="px-5 py-2 min-h-touch bg-[#920000] text-white font-medium rounded-lg shadow hover:bg-[#6F0000] transition duration-300 ease-in-out"
          >
            Dashboard
          </button>
          <button
            onClick={() => {
              logout()
              navigate('/login')
            }}
            className="px-5 py-2 min-h-touch bg-[#920000] text-white font-medium rounded-lg shadow hover:bg-[#6F0000] transition duration-300 ease-in-out"
          >
            Uitloggen
          </button>
        </div>
      </div>
    </div>
  )
}
